import {
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverTrigger,
  Portal,
  Tooltip,
  useBoolean,
} from '@chakra-ui/react';
import CheckCircleSharpIcon from '@gamma/ui/icons/CheckCircleSharpIcon';
import EditSharpIcon from '@gamma/ui/icons/EditSharpIcon';
import ForwardSharpIcon from '@gamma/ui/icons/ForwardSharpIcon';
import { AuthContext } from '@investigator/shared/providers';
import { useContext, useEffect, useState } from 'react';
import { IGetDetectionVariables } from '..';
import { i18n } from '../../../../shared/localization';
import { DetectionQueryVariables } from '../../pages';
import {
  CloseDetectionContent,
  SendToContent,
} from '../DetectionActions/PopoverContent';
import { useDetectionContext } from '../../context';

const { excludeEntity } = i18n.pages.entityDetails;

const { detectionIsClosed } = excludeEntity;

const { close, controls, detectionIsSent, sendTo, SNConfigureAndEnable } =
  i18n.pages.detections;

const { editStatus } = controls;

interface DetectionStatusActionsMenuProps {
  refetchMain?: boolean;
  isStatusHovered?: boolean;
  getDetectionVariables?: IGetDetectionVariables;
  queryDetectionsPaginatedVariables?: DetectionQueryVariables;
}

export const DetectionStatusActionsMenu = ({
  refetchMain,
  isStatusHovered,
  getDetectionVariables,
  queryDetectionsPaginatedVariables,
}: DetectionStatusActionsMenuProps) => {
  const [isEditing, setIsEditing] = useBoolean();

  const [popoverType, setPopoverType] = useState<null | string>(null);

  const [closeLoading, setCloseLoading] = useState<boolean>(false);
  const [sendToLoading, setSendToLoading] = useState<boolean>(false);

  const [isClosed, setIsClosed] = useState<boolean>(false);
  const [isSentTo, setIsSentTo] = useState<boolean>(false);

  const { userPrivileges, userPrivilegesLoading } = useContext(AuthContext);
  const { detectionData } = useDetectionContext();
  const tenantId = detectionData?.tenant_info?.tenant_id;
  const serviceNowEnabled = tenantId
    ? userPrivileges?.[tenantId]?.['service-now']
    : false;

  const escalationConfigurationsLoading = userPrivilegesLoading;

  useEffect(() => {
    setIsClosed(detectionData?.detection_status !== 'open');
    setIsSentTo(!!detectionData?.escalation_data?.escalation_status);
  }, [detectionData]);

  return (
    <Box>
      <Popover
        offset={[0, 10]}
        closeOnEsc={true}
        closeOnBlur={true}
        isOpen={isEditing}
        onOpen={setIsEditing.on}
        onClose={() => {
          setIsEditing.off();
          setPopoverType(null);
        }}
        placement="bottom-start"
      >
        {isEditing || isStatusHovered ? (
          <Menu
            onClose={() => {
              if (popoverType === null) {
                setIsEditing.off();
              }
            }}
            closeOnSelect={false}
          >
            <PopoverTrigger>
              <span>
                <Tooltip textStyle="body-md" label={editStatus}>
                  <MenuButton
                    as={IconButton}
                    w="24px"
                    h="24px"
                    paddingLeft={1}
                    size="box-md"
                    variant="solid"
                    colorScheme="gray"
                    data-testid="suppress-entity-popover-content-trigger"
                    aria-label={editStatus}
                    disabled={
                      (isClosed && isSentTo) ||
                      closeLoading ||
                      sendToLoading ||
                      escalationConfigurationsLoading ||
                      !serviceNowEnabled
                    }
                  >
                    <EditSharpIcon size="sm" />
                  </MenuButton>
                </Tooltip>
              </span>
            </PopoverTrigger>
            {popoverType === null && isEditing && (
              <Portal>
                <MenuList zIndex="popover">
                  <Tooltip
                    offset={[-9, 6]}
                    placement="left-start"
                    shouldWrapChildren={true}
                    label={isClosed ? detectionIsClosed : null}
                  >
                    <MenuItem
                      isDisabled={isClosed}
                      data-testid="close-detection-trigger"
                      onClick={() => {
                        setPopoverType('close');
                      }}
                      icon={<CheckCircleSharpIcon />}
                    >
                      {close}
                    </MenuItem>
                  </Tooltip>
                  <Tooltip
                    offset={[-9, 6]}
                    placement="left-start"
                    shouldWrapChildren={true}
                    label={
                      isSentTo
                        ? detectionIsSent
                        : !sendToLoading &&
                            !escalationConfigurationsLoading &&
                            !serviceNowEnabled
                          ? SNConfigureAndEnable
                          : null
                    }
                  >
                    <MenuItem
                      isDisabled={isSentTo || !serviceNowEnabled}
                      data-testid="send-to-trigger"
                      onClick={() => {
                        setPopoverType('sendTo');
                      }}
                      icon={<ForwardSharpIcon />}
                    >
                      {sendTo}
                    </MenuItem>
                  </Tooltip>
                </MenuList>
              </Portal>
            )}
          </Menu>
        ) : null}
        {popoverType === 'close' && isEditing && (
          <CloseDetectionContent
            refetchMain={refetchMain}
            onClose={setIsEditing.off}
            setIsClosed={setIsClosed}
            setIsLoading={setCloseLoading}
            setPopoverType={setPopoverType}
            getDetectionVariables={getDetectionVariables}
            queryDetectionsPaginatedVariables={
              queryDetectionsPaginatedVariables
            }
          />
        )}
        {popoverType === 'sendTo' && isEditing && (
          <SendToContent
            refetchMain={refetchMain}
            onClose={setIsEditing.off}
            setIsSentTo={setIsSentTo}
            setIsLoading={setSendToLoading}
            setPopoverType={setPopoverType}
            getDetectionVariables={getDetectionVariables}
            queryDetectionsPaginatedVariables={
              queryDetectionsPaginatedVariables
            }
          />
        )}
      </Popover>
    </Box>
  );
};
